import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export interface AppLanguage {
  code: string;
  label: string;
}

const STORAGE_KEY = 'jjames.lang';

/**
 * Thin wrapper around ngx-translate's TranslateService that owns the set of
 * supported languages, the current selection (as a signal), and persistence.
 * Register the dictionaries/loader via `provideTranslateService` in each app.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private translate = inject(TranslateService);

  /** Languages shipped by the apps. Keep in sync with the i18n JSON files. */
  readonly availableLanguages: AppLanguage[] = [
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' }
  ];

  /** Currently active language code. */
  readonly current = signal<string>('en');

  /**
   * Resolve and apply the initial language, awaiting the dictionary load so the
   * first render is already translated (no flash of keys). Call from an app initializer.
   */
  async init(): Promise<void> {
    const codes = this.availableLanguages.map(l => l.code);
    this.translate.addLangs(codes);
    await this.applyLang(this.resolveInitialLang(codes));
  }

  /** Switch language at runtime and persist the choice. */
  async use(code: string): Promise<void> {
    if (!this.availableLanguages.some(l => l.code === code)) return;
    await this.applyLang(code);
  }

  private async applyLang(code: string): Promise<void> {
    await firstValueFrom(this.translate.use(code));
    this.current.set(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* localStorage unavailable (private mode / SSR) — ignore */
    }
  }

  private resolveInitialLang(codes: string[]): string {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if (stored && codes.includes(stored)) return stored;

    const browser = this.translate.getBrowserLang();
    if (browser && codes.includes(browser)) return browser;

    return codes[0] ?? 'en';
  }
}
